import React, { useEffect } from "react";
import { Modal, Table } from "react-bootstrap";
import { SaveButton } from "./SaveButton";
import { PrintButton } from "./PrintButton";
import { Controller } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { CreateBillDto } from "../model/bill/dto/CreateBillDto";
import { CreationModalHeader } from "../CreationModalHeader";
import { CreateEditModalFooter } from "./CreateEditModalFooter";
import { Bill } from "../model/bill/types/Bill";
import { getInitialDto } from "../utils/getInitialDto";
import { UpdateBillDto } from "../model/bill/dto/UpdateBillDto";
import { SaveEmailButton } from "./SaveEmailButton";

type CreateEditModalProps = {
  open: boolean;
  onClose: VoidFunction;
  bill?: Bill | null;
};

export const CreateEditModal: React.FC<CreateEditModalProps> = ({
  open = false,
  onClose,
  bill,
}) => {
  const { control, watch, reset } = useFormContext<
    CreateBillDto | UpdateBillDto
  >();

  const billDetails = watch("billDetails");

  const editMode = Boolean(bill?._id);

  useEffect(() => {
    if (editMode) {
      if (open && bill) {
        reset({
          ...bill,
          invoiceDate: new Date(bill.invoiceDate).toISOString().split("T")[0],
          billDetails: Array.from(
            { length: 40 },
            (_, index) =>
              bill.billDetails?.find((b) => b.index === index) || {
                index,
                qty: "",
                code: "",
                description: "",
                unitPrice: "",
              }
          ),
        });
      }
    } else {
      reset(getInitialDto());
    }
  }, [open, bill, reset, editMode]);

  return (
    <Modal
      size="xl"
      show={open}
      onHide={onClose}
      dialogClassName="modal-90w create-modal"
      aria-labelledby="example-modal-sizes-title-lg"
      backdrop="static"
    >
      <Modal.Header closeButton style={{ justifyContent: "space-between" }}>
        <Modal.Title id="example-modal-sizes-title-lg">Create Bill</Modal.Title>
      </Modal.Header>

      <Modal.Body className="d-flex flex-column gap-3">
        <CreationModalHeader bill={bill} />

        <Table bordered responsive dir="ltr">
          <thead className="sticky-top shadow-sm">
            <tr>
              <th>#</th>
              <th className="th-small">QTY</th>
              <th className="th-small">CODE</th>
              <th>DESCRIPTION</th>
              <th className="th-small">UNIT PRICE</th>
              <th className="th-small">AMOUNT</th>
            </tr>
          </thead>

          <tbody className="table-body">
            {Array.from({ length: 40 }).map((_, index) => {
              const existingBill = billDetails?.find(
                (bill) => bill.index === index
              );

              return (
                <tr key={`${existingBill?._id}-${index}`}>
                  <td>{index + 1}</td>
                  <td>
                    <Controller
                      control={control}
                      name={`billDetails.${index}.qty`}
                      render={({ field: { value, onChange } }) => (
                        <input
                          type="text"
                          value={existingBill?.qty || ""}
                          onChange={(e) =>
                            onChange(e.target.value.replace(/[^0-9]/g, ""))
                          }
                          maxLength={5}
                        />
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      control={control}
                      name={`billDetails.${index}.code`}
                      render={({ field: { value, onChange } }) => (
                        <input
                          type="text"
                          value={existingBill?.code || ""}
                          onChange={(e) =>
                            onChange(e.target.value.replace(/[^0-9]/g, ""))
                          }
                          maxLength={5}
                        />
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      control={control}
                      name={`billDetails.${index}.description`}
                      render={({ field: { value, onChange } }) => (
                        <input
                          type="text"
                          value={existingBill?.description || ""}
                          onChange={onChange}
                          maxLength={75}
                        />
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      control={control}
                      name={`billDetails.${index}.unitPrice`}
                      render={({ field: { value, onChange } }) => (
                        <input
                          type="text"
                          value={existingBill?.unitPrice || ""}
                          onChange={(e) =>
                            onChange(e.target.value.replace(/[^0-9.]/g, ""))
                          }
                          maxLength={5}
                        />
                      )}
                    />
                  </td>
                  <td>
                    <span className="text-muted">
                      {Number(existingBill?.qty) &&
                      Number(existingBill?.unitPrice)
                        ? Number(
                            (
                              Number(existingBill?.qty) *
                              Number(existingBill?.unitPrice)
                            ).toFixed(2)
                          )
                        : ""}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        <CreateEditModalFooter />
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: "space-between" }}>
        {/* <ResetButton /> */}
        <div />
        <div style={{ gap: "1rem", display: "flex" }}>
          {!editMode ? <SaveEmailButton onClose={onClose} /> : null}
          <SaveButton onClose={onClose} editMode={editMode} />
          <PrintButton />
        </div>
      </Modal.Footer>
    </Modal>
  );
};
